import { useTranslation } from 'react-i18next';

type Valor = unknown;
type Traductor = (clau: string, opcions?: Record<string, unknown>) => string;

const IGNORATS = new Set([
  'version',
  'etag',
  'createdAt',
  'updatedAt',
  'ultimSync',
  'ultimAcces',
  'ultimaExecucio',
]);

const CLAUS_RESUM = [
  'nom',
  'nomCa',
  'titol',
  'codi',
  'upn',
  'email',
  'telefon',
  'versio',
  'estat',
];

const MAX_CANVIS = 12;

interface CanviCamp {
  camp: string;
  abans: Valor;
  despres: Valor;
}

function llegirJson(valor: string | null | undefined): Valor {
  if (valor == null || valor === '') return undefined;
  try {
    return JSON.parse(valor) as Valor;
  } catch {
    return valor;
  }
}

const esObjecte = (v: Valor): v is Record<string, Valor> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

const igual = (a: Valor, b: Valor) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Camps que han canviat entre dues instantànies (només el primer nivell; els objectes niats es comparen sencers). */
function calcularCanvis(previ: Valor, nou: Valor): CanviCamp[] {
  if (!esObjecte(previ) || !esObjecte(nou)) {
    if (previ === undefined || nou === undefined || igual(previ, nou)) return [];
    return [{ camp: '', abans: previ, despres: nou }];
  }
  const camps = new Set([...Object.keys(previ), ...Object.keys(nou)]);
  const canvis: CanviCamp[] = [];
  camps.forEach((camp) => {
    if (IGNORATS.has(camp)) return;
    if (!igual(previ[camp], nou[camp])) {
      canvis.push({ camp, abans: previ[camp], despres: nou[camp] });
    }
  });
  return canvis;
}

const humanitza = (camp: string) =>
  camp
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

function etiquetaCamp(camp: string, t: Traductor): string {
  return t(`auditoria.camps.${camp}`, { defaultValue: humanitza(camp) });
}

function textValor(valor: Valor, t: Traductor): string {
  if (valor === undefined || valor === null || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? t('auditoria.canvis.si') : t('auditoria.canvis.no');
  if (Array.isArray(valor)) {
    if (valor.length === 0) return '—';
    return valor.map((v) => textValor(v, t)).join(', ');
  }
  if (esObjecte(valor)) {
    const resum = CLAUS_RESUM.map((k) => valor[k]).find((v) => v != null && v !== '');
    if (resum != null) return String(resum);
    if (valor.id != null) return `#${String(valor.id)}`;
    return JSON.stringify(valor);
  }
  return String(valor);
}

function resumCreacio(valor: Valor): CanviCamp[] {
  if (!esObjecte(valor)) return [];
  return Object.keys(valor)
    .filter((k) => !IGNORATS.has(k) && k !== 'id')
    .filter((k) => {
      const v = valor[k];
      return v != null && v !== '' && !(Array.isArray(v) && v.length === 0) && !esObjecte(v);
    })
    .map((camp) => ({ camp, abans: undefined, despres: valor[camp] }));
}

interface Props {
  valorPrevi?: string | null;
  valorNou?: string | null;
  compacte?: boolean;
}

/** Detall llegible d'un registre d'auditoria: quins camps han canviat i el valor abans i després. */
export function CanvisAuditoria({ valorPrevi, valorNou, compacte }: Props) {
  const { t } = useTranslation('administracio');
  const previ = llegirJson(valorPrevi);
  const nou = llegirJson(valorNou);
  if (previ === undefined && nou === undefined) return null;

  let canvis: CanviCamp[];
  let titol: string | null = null;
  if (previ !== undefined && nou !== undefined) {
    canvis = calcularCanvis(previ, nou);
    if (canvis.length === 0) {
      return <div className="t-caption dim">{t('auditoria.canvis.senseCanvis')}</div>;
    }
  } else if (nou !== undefined) {
    canvis = resumCreacio(nou);
    titol = t('auditoria.canvis.dades');
    if (canvis.length === 0 && !esObjecte(nou)) {
      return <div className="t-caption dim">{textValor(nou, t)}</div>;
    }
  } else {
    canvis = resumCreacio(previ).map((c) => ({ ...c, abans: c.despres, despres: undefined }));
    titol = t('auditoria.canvis.eliminat');
  }
  if (canvis.length === 0) return null;
  const visibles = canvis.slice(0, compacte ? 6 : MAX_CANVIS);
  const restants = canvis.length - visibles.length;

  return (
    <div className="t-caption mt-4" data-testid="canvis-auditoria">
      {titol ? <div className="dim">{titol}</div> : null}
      <ul className="aud-canvis">
        {visibles.map((c) => (
          <li key={c.camp || 'valor'}>
            {c.camp ? <strong>{etiquetaCamp(c.camp, t)}: </strong> : null}
            {previ !== undefined && nou !== undefined ? (
              <>
                <span className="dim aud-canvis__abans">{textValor(c.abans, t)}</span>
                {' → '}
                <span>{textValor(c.despres, t)}</span>
              </>
            ) : (
              <span>{textValor(c.despres !== undefined ? c.despres : c.abans, t)}</span>
            )}
          </li>
        ))}
      </ul>
      {restants > 0 ? (
        <div className="dim">{t('auditoria.canvis.mes', { count: restants })}</div>
      ) : null}
    </div>
  );
}
